import httpStatus from 'http-status-codes';

import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import {
  fetchCategories as fetchCategoriesApi,
} from '../../../shared/api';

import {
  FETCH_CATEGORIES_LIST,
  SELECT_CATEGORY,
  FETCH_TAGS_LIST_FOR_CATEGORY,
  FETCH_TAGS_LIST_FOR_CATEGORY_SUCCESS,
  FETCH_TAGS_LIST_FOR_FAILURE,
  FETCH_SUB_TAGS_LIST_FOR_CATEGORY,
  FETCH_SUB_TAGS_LIST_FOR_CATEGORY_SUCCESS,
  FETCH_SUB_TAGS_LIST_FOR_FAILURE,
  SAVING_CATEGORY,
  SAVING_CATEGORY_SUCCESS,
  SAVING_CATEGORY_FAILURE,
  SAVING_TAG,
  SAVING_TAG_SUCCESS,
  SAVING_TAG_FAILURE,
  UPLOAD_TAGS,
  UPLOAD_TAGS_FAILURE,
  IMPORT_TAGS,
  IMPORT_TAGS_SUCCESS,
  IMPORT_TAGS_FAILURE,
  UPLOAD_TAGS_SUCCESS,
  CLEAR_IMPORTED_TAGS_ENTITIES,
  DELETE_TAG,
  DELETE_TAG_SUCCESS,
  DELETE_TAG_FAILURE,
} from './types';

import {
  fetchTags as fetchTagsApi,
  addCategory as addCategoryApi,
  editCategory as editCategoryApi,
  deleteCategory as deleteCategoryApi,
  addTag as addTagApi,
  updateTag as updateTagApi,
  deleteTag as deleteTagApi,
  fetchCategoryViews as fetchCategoryViewsApi,
  validateTags as validateTagsApi,
  importTags as importTagsApi,
} from '../api';

import { fetchingError, showAlert } from '../../../shared/store/actions';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';

import ModalNames from '../modalNames';
import getModalActions from '../../../shared/modals/getModalActions';
import agGridConfig from '../../../agGridConfig';
import alertTypes from '../../../shared/alertTypes';
import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';
import {
  FIELD_MAPPINGS,
  IMPORT_RANGE_LAST_COLUMN,
  MAX_COLUMNS_NUMBER,
  MIN_COLUMNS_NUMBER, SHEET_NAME,
} from '../xlsxImport/constants';
import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import currentPeriodValidator from '../../../shared/xlsxImport/validators/currentPeriod.validator';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';
import columnHeaders from '../xlsxImport/columnHeaders';
import { ADD_OR_UPDATE_VALUES } from '../../shared/constants';
import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';
import { normalizeCellToString } from '../../../utils/readXlsxFile.utils';

export const fetchCategoriesForCategoriesAndTags = period => async dispatch => {
  try {
    const categoriesList = await fetchCategoriesApi(period.name);
    const categoryViews = await fetchCategoryViewsApi();

    return dispatch({
      type: FETCH_CATEGORIES_LIST,
      categoriesList,
      categoryViews,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const selectCategory = category => dispatch => {
  dispatch({
    type: SELECT_CATEGORY,
    category,
  });
};

export const fetchTagsForCategoriesAndTags = (period, categoryName) => async dispatch => {
  dispatch({ type: FETCH_TAGS_LIST_FOR_CATEGORY });

  try {
    const tagsList = await fetchTagsApi(period, categoryName);
    dispatch({
      type: FETCH_TAGS_LIST_FOR_CATEGORY_SUCCESS,
      tagsList,
    });
  } catch (error) {
    if (!error.isFetchingError) {
      throw error;
    }

    if (error.status === httpStatus.NOT_FOUND) {
      return dispatch({
        type: FETCH_TAGS_LIST_FOR_FAILURE,
        errorMessage: error.message,
      });
    }

    dispatch(fetchingError());
    dispatch(errorNotification(error.message));
  }
};

export const fetchSubTagsForCategoriesAndTags = (period, categoryName) => async dispatch => {
  dispatch({ type: FETCH_SUB_TAGS_LIST_FOR_CATEGORY });

  try {
    const subTagsList = await fetchTagsApi(period, categoryName);
    dispatch({
      type: FETCH_SUB_TAGS_LIST_FOR_CATEGORY_SUCCESS,
      subTagsList,
    });
  } catch (error) {
    if (!error.isFetchingError) {
      throw error;
    }

    if (error.status === httpStatus.NOT_FOUND) {
      return dispatch({
        type: FETCH_SUB_TAGS_LIST_FOR_FAILURE,
        errorMessage: error.message,
      });
    }

    dispatch(fetchingError());
    dispatch(errorNotification(error.message));
  }
};

export const selectCategoryForCategoriesAndTags = (period, category) => dispatch => {
  dispatch(selectCategory(category.categoryName));
  dispatch(fetchTagsForCategoriesAndTags(period.name, category.categoryName));
  dispatch(fetchSubTagsForCategoriesAndTags(period.name, category.subCategoryName));
};

export const {
  showAction: showCategoryModalForAddEdit,
  hideAction: hideCategoryModalForAddEdit,
} = getModalActions(ModalNames.CategoryAddEdit);

export const {
  showAction: showCategoryModalForDelete,
  hideAction: hideCategoryModalForDelete,
} = getModalActions(ModalNames.CategoryDelete);

export const {
  showAction: showCategoryModalForRefusedDeleting,
  hideAction: hideCategoryModalForRefusedDeleting,
} = getModalActions(ModalNames.CategoryDeleteRefused);

export const saveCategory = ({
  categoryData,
  periodName,
}, openForEdit, { callback, keepOpenAfterSaving }) => async dispatch => {
  if (!periodName) {
    return;
  }

  dispatch({
    type: SAVING_CATEGORY,
  });

  try {
    if (!openForEdit) {
      await addCategoryApi({
        periodName,
        categoryData,
      });
      dispatch(successNotification('Category added'));
    } else {
      await editCategoryApi({
        periodName,
        categoryData,
      });
      dispatch(successNotification('Category updated'));
    }

    dispatch({
      type: SAVING_CATEGORY_SUCCESS,
    });

    if (callback) {
      callback();
    }

    await dispatch(fetchCategoriesForCategoriesAndTags({ name: periodName }));
    dispatch(selectCategoryForCategoriesAndTags({ name: periodName }, categoryData));

    if (keepOpenAfterSaving) {
      return;
    }

    return dispatch(hideCategoryModalForAddEdit());
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: SAVING_CATEGORY_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const deleteCategory = ({ period, categoryName }) => async dispatch => {
  try {
    await deleteCategoryApi({ periodName: period.name, categoryName });
    dispatch(successNotification('Category deleted'));
    dispatch(hideCategoryModalForDelete());

    await dispatch(fetchCategoriesForCategoriesAndTags(period));
    return dispatch(selectCategory(null));
  } catch (error) {
    if (error.isFetchingError && error.status === httpStatus.BAD_REQUEST) {
      dispatch(hideCategoryModalForDelete());
      return dispatch(showCategoryModalForRefusedDeleting());
    }
    dispatch(errorNotification(error.message));
  }
};

export const {
  showAction: showTagsModalForAddEdit,
  hideAction: hideTagsModalForAddEdit,
} = getModalActions(ModalNames.TagAddEdit);

export const {
  showAction: showTagsModalForRefusedDeleting,
  hideAction: hideTagsModalForRefusedDeleting,
} = getModalActions(ModalNames.TagDeleteRefused);

export const {
  showAction: showTagsModalForDelete,
  hideAction: hideTagsModalForDelete,
} = getModalActions(ModalNames.TagDelete);

export const saveTag = ({
  tagData,
  periodName,
  category,
}, openForEdit, { callback, keepOpenAfterSaving }) => async dispatch => {
  if (!periodName || !category) {
    return;
  }

  dispatch({
    type: SAVING_TAG,
  });

  try {
    if (!openForEdit) {
      await addTagApi({
        periodName,
        category,
        tagData,
      });
      dispatch(successNotification('Tag added'));
    } else {
      await updateTagApi({
        periodName,
        category,
        tagData,
      });
      dispatch(successNotification('Tag updated'));
    }

    dispatch({
      type: SAVING_TAG_SUCCESS,
    });
    if (callback) {
      callback();
    }

    await dispatch(fetchTagsForCategoriesAndTags(periodName, category));
    if (keepOpenAfterSaving) {
      return;
    }

    dispatch(selectRow({
      name: agGridConfig.adminCategoriesAndTags.name,
      fields: agGridConfig.adminCategoriesAndTags.fields,
      data: {
        tagName: tagData.tagName,
      },
    }));

    return dispatch(hideTagsModalForAddEdit());
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: SAVING_TAG_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const deleteTag = ({
  tagName,
  periodName,
  categoryName,
}, {
  rowIndex,
}) => async dispatch => {
  dispatch({ type: DELETE_TAG });
  try {
    await deleteTagApi({
      periodName,
      categoryName,
      tagName,
    });

    dispatch(successNotification('Tag deleted'));
    dispatch(hideTagsModalForDelete());

    await dispatch(fetchTagsForCategoriesAndTags(periodName, categoryName));
    dispatch({ type: DELETE_TAG_SUCCESS });
    return dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));
  } catch (error) {
    dispatch({ type: DELETE_TAG_FAILURE });
    if (error.isFetchingError) {
      if (error.status === httpStatus.BAD_REQUEST) {
        dispatch(hideTagsModalForDelete());
        dispatch(showTagsModalForRefusedDeleting());
      } else {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
      }
      return;
    }
    throw error;
  }
};

export const uploadTags = ({ file, period, category }) => async dispatch => {
  dispatch({
    type: UPLOAD_TAGS,
  });

  try {
    const uploadedTags = await readXlsxFileAsJson({
      file,
      fileValidators: [excelFileTypeValidator],
      prevalidators: [columnHeadersValidator],
      postvalidators: [noEmptyRowsValidator, currentPeriodValidator],
      preprocessors: [emptyRowsTrimmer],
      fieldNormalizers: {
        description: normalizeCellToString,
        tagname: normalizeCellToString,
      },
      fieldMappings: FIELD_MAPPINGS,
      sheetName: SHEET_NAME,
      getRange: calculateImportRangeFactory(IMPORT_RANGE_LAST_COLUMN),
      mapToJsonOptions: {
        header: columnHeaders,
      },
      validationData: {
        columnHeaders,
        currentPeriod: period,
        minColumnsNumber: MIN_COLUMNS_NUMBER,
        maxColumnsNumber: MAX_COLUMNS_NUMBER,
      },
    });

    const validationErrors = await validateTagsApi({
      tags: uploadedTags,
      categoryName: category.categoryName,
      periodName: period.name,
    });

    if (validationErrors) {
      Object.keys(validationErrors).forEach(errorIndex => {
        uploadedTags[errorIndex].validationData = validationErrors[errorIndex];
      });
    }

    dispatch({
      type: UPLOAD_TAGS_SUCCESS,
      uploadedTags,
    });
    dispatch(successNotification('File Upload Successful'));
  } catch (error) {
    dispatch({
      type: UPLOAD_TAGS_FAILURE,
    });

    if (error.isValidationError) {
      dispatch(showAlert(error.message, alertTypes.DANGER));
    } else {
      dispatch(errorNotification(error.message));
    }
  }
};

export const importTags = ({ tags, category, period }) => async dispatch => {
  dispatch({ type: IMPORT_TAGS });

  const filteredTags = tags
    .filter(entity => entity.addOrUpdate !== ADD_OR_UPDATE_VALUES.NO_CHANGE);

  try {
    await importTagsApi({
      tags: filteredTags,
      categoryName: category.categoryName,
      periodName: period.name,
    });
    dispatch(successNotification('Tags Import Successful'));
    dispatch({
      type: IMPORT_TAGS_SUCCESS,
    });
  } catch (error) {
    dispatch({ type: IMPORT_TAGS_FAILURE });
    dispatch(showAlert(error.message, alertTypes.DANGER));
  }
};

export const clearImportedTagsEntities = () => ({ type: CLEAR_IMPORTED_TAGS_ENTITIES });
